/*
    Sermon hub pages (HAP-4387): a speaker, church or series gets its own
    crawlable page under /sermons — /sermons/speakers/:id/:slug etc. — instead
    of the old `/sermons?filters={json}` view state, which search engines treat
    as one page and people can't read.

    The id is the key (slugs are NOT unique in the CMS: five different series
    are all `acts`). The slug is cosmetic — it puts the name in the URL — and is
    derived from the title here rather than read from the record, so every link,
    the SSR canonical (front/public/index.js) and the sitemap agree on exactly
    one URL per hub. Keep `slugify` in lockstep with the copy in public/index.js.
*/
import algoliasearch from 'algoliasearch/lite'
import axios from 'axios'
import { algoliaAppId, algoliaSearchKey, algoliaIndex, port, useDevAPI } from '../../../config/config/settings.json'

export const HUB_TYPES = {
    speaker: {
        segment: 'speakers',
        collection: 'people',
        facet: 'speakers',
        label: 'Speaker',
        plural: 'Speakers',
        icon: 'fal fa-microphone',
    },
    church: {
        segment: 'churches',
        collection: 'churches',
        facet: 'church',
        label: 'Church',
        plural: 'Churches',
        icon: 'fal fa-church',
    },
    series: {
        segment: 'series',
        collection: 'sermonSeries',
        facet: 'series',
        label: 'Series',
        plural: 'Series',
        icon: 'fal fa-layer-group',
    },
}

export function slugify(title) {
    return String(title || '')
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

export function hubPath(type, entity) {
    const config = HUB_TYPES[type]
    if (!config || !entity?.id) return '/sermons'
    const slug = slugify(entity.title || entity.name)
    return `/sermons/${config.segment}/${entity.id}${slug ? `/${slug}` : ''}`
}

export const speakerPath = speaker => hubPath('speaker', speaker)
export const churchPath = church => hubPath('church', church)
export const seriesPath = series => hubPath('series', series)

// Sermons are only ever public on these pages; the hubs are for visitors and
// crawlers, not a church admin's private feed.
// `id` may be a list: on the HAP-4515 preview one speaker's hub covers their
// duplicate records ("Logan Hagoort" + "Rev. Logan Hagoort").
export const hubFilter = (type, id) => {
    const ids = [].concat(id).filter(Boolean)
    const facet = `${HUB_TYPES[type].facet}.id`
    const scope = ids.length > 1 ? `(${ids.map(i => `${facet}:${i}`).join(' OR ')})` : `${facet}:${ids[0]}`
    return `${scope} AND private:false`
}

const index = algoliasearch(algoliaAppId, algoliaSearchKey).initIndex(algoliaIndex)

// One zero-hit Algolia query: the exact public sermon count for a hub plus the
// facet counts used for its related links (e.g. a speaker's series).
export async function hubFacets(type, id, facets = []) {
    const response = await index.search('', {
        filters: `collection:sermons AND ${hubFilter(type, id)}`,
        facets,
        maxValuesPerFacet: 100,
        hitsPerPage: 0,
    })
    return { count: response.nbHits || 0, facets: response.facets || {} }
}

// Every speaker/church/series with at least one public sermon, with its count.
export async function directoryFacet(type) {
    const facet = `${HUB_TYPES[type].facet}.id`
    const response = await index.search('', {
        filters: 'collection:sermons AND private:false',
        facets: [facet],
        maxValuesPerFacet: 1000,
        hitsPerPage: 0,
    })
    return response.facets?.[facet] || {}
}

// Page <title> + meta description for a client-side navigation. The SSR server
// writes the same tags on a hard load; this keeps them right after in-app
// navigation too (Google renders the SPA).
export function setPageMeta({ title, description, canonical }) {
    if (title) document.title = title
    const upsert = (selector, create) => {
        let el = document.head.querySelector(selector)
        if (!el) { el = create(); document.head.appendChild(el) }
        return el
    }
    if (description) {
        upsert('meta[name="description"]', () => Object.assign(document.createElement('meta'), { name: 'description' }))
            .setAttribute('content', description)
    }
    if (canonical) {
        upsert('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }))
            .setAttribute('href', canonical)
    }
}

// Speaker extras for the HAP-4515 preview (church, latest sermon, surname sort,
// duplicate groups, placeholders) from /controllers/sermonSpeakerDirectory.
// One request per page load, shared by the directory and the hub pages.
let speakerDirectoryRequest = null
export function speakerDirectory() {
    let api = 'https://api.churchandfamilylife.com'
    if (process.env.NODE_ENV != 'production' && useDevAPI) api = `http://localhost:${port}`
    speakerDirectoryRequest = speakerDirectoryRequest || axios.get(`${api}/controllers/sermonSpeakerDirectory`)
        .then(r => (r.data?.success ? r.data.speakers : []))
        .catch(() => { speakerDirectoryRequest = null; return [] })
    return speakerDirectoryRequest
}
